<template>
  <div>
    <div class="layout">
      <div class="column">
        <h1 v-if="config.title" class="title">
          {{ $t(config.title, "options.title") }}
          <span
            class="tooltip"
            v-if="this.config.tooltip"
            :content="$t(config.tooltip, 'tooltip')"
            ><ion-icon name="help-circle-outline"></ion-icon
          ></span>
        </h1>
        <h1 key="question">{{ $t(config.question, "options.question") }}</h1>
        <h2 class="helpText">{{ $t(helpText, "options.helpText") }}</h2>
      </div>
      <div
        :data-polling="polling"
        :class="['column multi-select-column', columnStyle]"
      >
        <!--<div v-if='this.config.maxAnswers'>
          <p>{{guidanceText}}</p>
        </div>-->
        <transition-group
          appear
          name="options"
          v-on:before-appear="beforeEnter"
          tag="ul"
          class="question-set"
          v-bind:css="false"
        >
          <li
            role="button"
            :tabindex="i + 1"
            v-on:keyup.enter.stop="onEditOrSelect(answer)"
            :style="buttonStyles[answer.value]"
            :data-index="i"
            v-bind:class="{
              'answer-button': true,
              selected: isSelected(answer.value)
            }"
            :aria-pressed="isSelected(answer.value)"
            ref="answerButton"
            @click.stop="onEditOrSelect(answer)"
            :data-a="answer.key"
            :data-c="answer.score > 0"
            :key="config.question.key + '-answer-' + i"
            v-for="(answer, i) in config.answers"
          >
            <span class="label" v-show="activeOther !== answer.key"
              >{{ $t(answer.label, `options.answers.${answer.key}.label`) }}
              <span v-if="answer.other && others[answer.key]"
                >: {{ others[answer.key] }}</span
              ></span
            >

            <span
              class="other"
              v-if="answer.other && activeOther === answer.key && editingOther"
            >
              <span class="textarea" v-if="activeOther == answer.key">
                <textarea
                  :ref="`other_${answer.key}`"
                  :placeholder="answer.otherPlaceholder || 'Specify other'"
                  v-model="others[answer.key]"
                ></textarea>
              </span>
              <span class="done" v-if="activeOther == answer.key">
                <a @click.prevent.stop="clearOther(answer)"
                  ><ion-icon name="close-outline"></ion-icon
                ></a>
                <a
                  v-if="others[answer.key] && others[answer.key].trim() !== ''"
                  @click.prevent.stop="completeOther(answer)"
                  ><ion-icon name="checkmark-outline"></ion-icon
                ></a>
              </span>
            </span>

            <span
              class="icon"
              v-show="activeOther !== answer.key"
              :style="
                isSelected(answer.value) ? theme.selectedIcon : theme.icons
              "
            >
              <ion-icon
                class="icon--correct"
                :style="{
                  color: isSelected(answer.value)
                    ? theme.selected.backgroundColor
                    : null
                }"
                name="checkmark-circle-outline"
              ></ion-icon>
              <ion-icon
                class="icon--incorrect"
                name="close-circle-outline"
              ></ion-icon>
              <span class="percent"></span>
            </span>

            <span class="bar"></span>
          </li>
          <li
            v-on:keyup.enter.stop="next"
            tabindex="999"
            role="button"
            :data-index="config.answers.length"
            :style="nextButtonStyle"
            @click.stop="next"
            class="next"
            key="next"
          >
            {{ $t(buttonText, "options.buttonText") }}
          </li>
        </transition-group>
      </div>
    </div>
  </div>
</template>

<script>
import Velocity from "velocity-animate";
import TextHelper from "../lib/TextHelper";
import Vue from "vue";
import TranslationMixin from "../lib/TranslationMixin";

export default {
  name: "MultiSelect",
  props: ["config"],
  mixins: [TranslationMixin],
  inject: ["convince", "theme"],
  data: function() {
    return {
      currentSelection: [],
      selectedKeys: [],
      buttonStyles: {},
      corrects: {},
      incorrects: {},
      hideIcons: false,
      complete: false,
      polling: false,
      others: {},
      activeOther: null,
      editingOther: false
    };
  },
  computed: {
    guidanceText() {
      let moreAnswer = this.$t("more answer", "more answer");
      let moreAnswers = this.$t("more answers", "more answers");
      let noMoreAnswers = this.$t("No more answers", "no more answers");
      let select = this.$t("Select", "select");
      let remaining = this.config.maxAnswers - this.currentSelection.length;
      if (remaining === 0) {
        return `${noMoreAnswers}`;
      }
      let answerInflected = TextHelper.inflect(
        remaining,
        moreAnswer,
        moreAnswers
      );
      return `${select} ${remaining} ${answerInflected}`;
    },
    leaveDelay() {
      let d = this.config.leaveDelay || this.theme.leaveDelay || 2000;
      return d;
    },
    columnStyle() {
      return "multi-select-columns-" + this.columnCount;
    },
    nextButtonStyle() {
      if (!this.isValid) {
        return { opacity: 0.5, borderRadius: this.theme.borderRadius + "px" };
      }
      return Object.assign(this.theme.accent, {
        borderRadius: this.theme.borderRadius + "px"
      });
    },
    isValid() {
      if (this.editingOther) {
        return false;
      }
      if (this.config.required && this.currentSelection.length === 0) {
        return false;
      }
      if (
        this.config.minAnswers &&
        this.currentSelection.length < this.config.minAnswers
      ) {
        return false;
      }
      return true;
    },
    buttonText() {
      return this.config.buttonText || "Next";
    },
    helpText() {
      return this.config.helpText || "(Select all that apply)";
    }
  },
  methods: {
    markAnswers() {
      if (this.config.poll || this.config.mode === "poll") {
        this.markPollAnswers();
      } else if (this.config.mode === "quiz") {
        this.markQuizAnswers();
      } else {
        setTimeout(this.leave, 300);
      }
    },
    markQuizAnswers() {
      this.config.answers.forEach((answer) => {
        if (answer.score === 0) {
          this.incorrects[answer.value] = true;
        } else {
          this.corrects[answer.value] = true;
        }
      });
      const children = this.$el.querySelectorAll(
        ".question-set li.answer-button"
      );
      let styleChanges;
      children.forEach((el, i) => {
        if (el.dataset.c) {
          el.classList.add("correct");
          styleChanges = this.theme.correct;
        } else if (this.selectedKeys.indexOf(el.dataset.a) !== -1) {
          styleChanges = this.theme.incorrect;
          el.classList.add("incorrect");
        } else {
          styleChanges = false;
        }
        let options = { duration: 100 };
        if (i === children.length - 1) {
          options.complete = setTimeout(this.leave, this.leaveDelay);
        }
        if (styleChanges) {
          Velocity(el, styleChanges, options);
        }
      });
      this.hideIcons = false;
    },
    markPollAnswers() {
      this.polling = true;
      this.convince.polling(this.config.key).then(
        function(resp) {
          const children = this.$el.querySelectorAll(
            ".question-set li:not(.next)"
          );
          children.forEach(
            function(el, i) {
              let styleForThisItem =
                this.selectedKeys.indexOf(el.dataset.a) !== -1
                  ? "selected"
                  : null;
              let styleChanges = {};
              if (styleForThisItem) {
                styleChanges = Object.assign(
                  styleChanges,
                  this.theme[styleForThisItem]
                );
              }
              let bar = el.querySelector(".bar");
              for (let prop in resp.data) {
                let percent = resp.data[prop].percent * 100;
                let key = resp.data[prop].key;
                if (el.dataset.a === key) {
                  this.config.answers.forEach(function(answer, i) {
                    for (let item in resp.data) {
                      if (answer.value === item) {
                        el.answer = answer;
                        answer.percent = percent;
                      }
                    }
                  });
                  styleChanges = Object.assign(styleChanges, {
                    width: percent + "%"
                  });
                }
              }
              let animationTime = 1000;
              let options = {
                duration: animationTime,
                delay: false,
                easing: "none"
              };
              if (i === children.length - 1) {
                options.complete = setTimeout(this.leave, this.leaveDelay);
              }
              if (styleChanges) {
                Velocity(bar, styleChanges, options);
                if (parseFloat(el.answer.percent.toFixed(0)) > 0) {
                  Velocity(
                    el.querySelector("span.percent"),
                    { opacity: 1, tween: [el.answer.percent.toFixed(0), 0] },
                    {
                      duration: animationTime,
                      progress: function(
                        elements,
                        complete,
                        remaining,
                        start,
                        tweenValue
                      ) {
                        elements[0].innerText = tweenValue.toFixed(0) + "%";
                      },
                      queue: false
                    }
                  );
                } else {
                  el.querySelector("span.percent").innerText = "0%";
                }
              }
            }.bind(this)
          );
        }.bind(this)
      );
      this.hideIcons = false;
    },
    addBackgroundImage() {
      return Object.assign(
        {},
        { backgroundImage: "url(" + this.config.background + ")" }
      );
    },
    isSelected(selection) {
      return this.currentSelection.includes(selection);
    },
    addHover(i, event) {
      event.target.style.backgroundColor = this.theme.selected.backgroundColor;
      event.target.style.color = this.theme.selected.color;
      let icon = event.target.querySelector("span.icon");
      icon.classList.add("selected-icon");
      event.currentTarget.classList.add("hover");
    },
    removeHover(i, event) {
      if (event.currentTarget.classList.contains("selected")) {
        return;
      }
      event.target.style.backgroundColor = null;
      event.target.style.color = null;
      event.currentTarget.classList.remove("hover");
      let icon = event.target.querySelector("span.icon");
      icon.classList.remove("selected-icon");
    },
    clearOther(answer) {
      this.others[answer.key] = null;
      this.cancelOther();
      if (this.currentSelection.indexOf(answer.value) !== -1) {
        this.select(answer);
      }
    },
    cancelOther() {
      this.activeOther = null;
      this.editingOther = false;
    },
    completeOther(answer) {
      let isUnselected = this.currentSelection.indexOf(answer.value) === -1;
      if (isUnselected) {
        this.select(answer);
      }
      this.cancelOther();
    },
    onEditOrSelect(answer) {
      if (this.editingOther) {
        return;
      }
      if (answer.other) {
        this.activeOther = answer.key;
        this.editingOther = true;
        Vue.nextTick(() => {
          this.$refs[`other_${answer.key}`][0].focus();
          return;
        });
      } else {
        this.editingOther = false;
        this.activeOther = null;
      }
      this.select(answer);
    },
    select(answer) {
      let idx = this.currentSelection.indexOf(answer.value);
      let styles = { borderRadius: this.theme.borderRadius + "px" };
      this.buttonStyles[answer.value] = styles;
      if (idx !== -1) {
        this.currentSelection.splice(idx, 1);
        this.selectedKeys.splice(idx, 1);
        this.$gtag.event("survey", {
          event_category: "deselect",
          event_label: this.config.key,
          value: answer.value
        });
      } else {
        if (
          this.config.maxAnswers &&
          this.currentSelection.length >= this.config.maxAnswers
        ) {
          return;
        }
        this.currentSelection.push(answer.value);
        this.selectedKeys.push(answer.key);
        this.$gtag.event("survey", {
          event_category: "select",
          event_label: this.config.key,
          value: answer.value
        });
      }
    },
    next() {
      if (this.complete === true || !this.isValid) {
        return;
      }
      this.complete = true;
      this.$emit("answer");
      this.$gtag.event("answer", {
        event_category: "survey",
        event_label: this.config.key,
        value: this.currentSelection
      });
      let answerData = {
        value: this.currentSelection,
        score: null,
        _other: this.others
      };
      answerData.score = 0;
      this.config.answers.forEach((answer) => {
        this.currentSelection.forEach((selected) => {
          if (selected === answer.value) {
            answerData.score += answer.score;
          }
        });
      });

      this.$store
        .dispatch("save", { key: this.config.key, answer: answerData })
        .then(() => {
          this.markAnswers();
        });
    },
    leave() {
      for (let answer of this.config.answers) {
        if (this.currentSelection.indexOf(answer.value) !== -1 && answer.next) {
          return this.$store.commit("setIndex", this.getNext(answer.next));
        }
      }
      if (typeof this.config.next !== "undefined" && this.config.next) {
        this.$store.commit("setIndex", this.getNext(this.config.next));
      } else {
        this.$store.commit("advance");
      }
    },
    mergeProperties(ob, newOb) {
      for (let prop in newOb) {
        Vue.set(ob, prop, newOb[prop]);
      }
    },
    beforeEnter: function(el, done) {
      Velocity(
        el,
        { opacity: 0, translateY: "100%", translateZ: 0 },
        { duration: 0 }
      );
      var delay = el.dataset.index * 150;
      setTimeout(function() {
        Velocity(
          el,
          { opacity: 1, translateY: "0%", translateZ: 0 },
          { complete: done, duration: 550 }
        );
      }, delay);
    }
  },
  created() {
    this.currentSelection = [];
    this.convince.answers = this.config.answers;
    if (typeof this.config.columns !== "undefined") {
      this.columnCount = this.config.columns;
    } else {
      this.columnCount = 1;
    }
    for (let answer of this.config.answers) {
      this.buttonStyles[answer.value] = {
        borderRadius: this.theme.borderRadius + "px"
      };
      if (answer.other) {
        Vue.set(this.others, answer.key, null);
      }
    }
  },
  mounted() {
    window.scroll(0, 0);
  }
};
</script>

<style lang="scss">
@import "../scss/components/survey";
</style>

<style lang="scss" scoped>
span.label {
  position: static;
  z-index: 2;
}

span.icon ion-icon {
  visibility: hidden;
}

li.selected {
  span.icon ion-icon {
    visibility: visible;
  }
}

div[data-polling="true"] {
  span.icon ion-icon {
    visibility: hidden;
  }
}

span.percent {
  opacity: 1;
  z-index: 2;
}
span.bar {
  display: block;
  position: absolute;
  z-index: 1;
  left: 0;
  top: 0;
  bottom: 0;
  width: 0%;
}

li.answer-button {
  .icon--incorrect {
    display: none;
  }
  &.incorrect {
    .icon--incorrect {
      display: inline;
    }
    .icon--correct {
      display: none;
    }
  }
  &.correct {
    .icon--correct {
      display: inline;
    }
    .icon--incorrect {
      display: none;
    }
  }
}
</style>
